import React, { FC } from 'react';
import { SubtractUniversal } from '@duxton-web/icons';
import { Empty, Skeleton } from 'antd';
import styled from 'styled-components';

import { Experiment, RolloutTarget } from '../../../lib/App';
import { VariableSearchColumn } from './VariablesSearch';
import VariablesSearchExperiment, {
  filterExperimentsMatchingFacet,
} from './VariablesSearchExperiment';
import VariablesSearchMore from './VariablesSearchMore';
import VariablesSearchRollout, {
  filterRolloutTargetMatchingFacet,
} from './VariablesSearchRollout';
import VariablesSearchVariableColumn from './VariablesSearchVariableColumn';

export const TOTAL_RETURNED_RESULT = 21;

const VariablesSearchRecords: FC<{
  records: any[];
  isLoading: boolean;
  loadingColIndex: number;
  columns: VariableSearchColumn[];
  searchVal: string;
  filters: { [key: string]: any };
  pageNumber: number;
  total: number;
  hideFilter?: boolean;
  hideSearch?: boolean;
}> = ({
  columns,
  records,
  isLoading,
  loadingColIndex,
  searchVal,
  filters,
  pageNumber,
  total,
  hideFilter,
  hideSearch,
}) => {
  const calculateEmptyWrapperHeight = () => {
    const mainPadding = '48px';
    const mainTitleHeight = '28px';
    const tableTitleHeight = '45px';
    const tableConstraintsHeight = '88px';
    const gap = '16px';
    const emptyWrapperPaddingb = '32px';

    return `calc(100vh - ${mainPadding} - ${mainTitleHeight} - ${tableTitleHeight} - ${tableConstraintsHeight} - ${gap} - ${emptyWrapperPaddingb})`;
  };

  const renderColumnSkeleton = () => {
    const items = [];

    for (let i = 0; i < records?.length; i += 1) {
      items.push(
        <Skeleton
          key={`variables-search-records-column-loader-${i + 1}`}
          loading
          paragraph={{
            rows: 1,
          }}
        />,
      );
    }

    return <>{items}</>;
  };

  const renderRowContent = (
    record,
    constraintValue: string,
    colIndex: number,
  ) => {
    if (loadingColIndex === colIndex) return renderColumnSkeleton();

    if (!constraintValue) return <SubtractUniversal />;

    const rollout = record?.rollouts;
    const experiments = record?.experiments;
    const matchingExperiments: Map<number, Experiment> =
      filterExperimentsMatchingFacet(experiments, columns, colIndex);
    const experimentsCount =
      Array.from(matchingExperiments.keys())?.length || 0;

    const matchingRolloutTarget: Map<number, RolloutTarget> =
      filterRolloutTargetMatchingFacet(
        rollout?.rollouts,
        columns,
        colIndex,
        TOTAL_RETURNED_RESULT - experimentsCount,
      );
    const rolloutTargetsCount =
      Array.from(matchingRolloutTarget.keys())?.length || 0;

    return (
      <>
        <VariablesSearchExperiment
          variable={record}
          matchingExperiments={matchingExperiments}
          colIndex={colIndex}
          columns={columns}
        />
        <VariablesSearchRollout
          variable={record}
          matchingRolloutTarget={matchingRolloutTarget}
          colIndex={colIndex}
          columns={columns}
          renderAll={
            rolloutTargetsCount + experimentsCount <= TOTAL_RETURNED_RESULT - 1
          }
          searchVal={searchVal}
          filters={filters}
          pageNumber={pageNumber}
          total={total}
        />
        <VariablesSearchMore
          variable={record}
          colIndex={colIndex}
          columns={columns}
          total={rolloutTargetsCount + experimentsCount}
        />
      </>
    );
  };

  const renderRow = (record, index) => {
    const items = [
      <VariablesSearchVariableColumn
        key={`row-${index}-cell-0`}
        record={record}
        index={index}
        fixedComponent={true}
        hideFilter={hideFilter}
        hideSearch={hideSearch}
      />,
      columns.map((column, colIndex) => {
        if (loadingColIndex === colIndex) {
          if (index === 0)
            return (
              <td
                key={`row-${index}-cell-${colIndex + 1}`}
                rowSpan={records?.length}
                style={{
                  borderLeft:
                    colIndex === loadingColIndex && index === 0
                      ? '1px solid #E8E8E8'
                      : '0',
                }}
              >
                <div className="flex-col half-gap w100">
                  {renderRowContent(record, column.constraint.value, colIndex)}
                </div>
              </td>
            );

          return null;
        }

        return (
          <td
            key={`row-${index}-cell-${colIndex + 1}`}
            style={{
              borderRight:
                colIndex + 1 === loadingColIndex ? '0' : '1px solid #E8E8E8',
            }}
          >
            <div className="flex-col half-gap w100">
              {renderRowContent(record, column.constraint.value, colIndex)}
            </div>
          </td>
        );
      }),
    ];

    return <tr key={`variables-search-body-row-${index}`}>{items}</tr>;
  };

  const renderBody = () => {
    if (isLoading)
      return (
        <tbody>
          <tr>
            <td
              colSpan={2}
              style={{
                borderTop: '1px solid #E8E8E8',
              }}
            >
              <div
                style={{
                  height: 'calc(100vh - 230px)',
                  padding: '24px',
                  overflow: 'hidden',
                }}
              >
                <Skeleton active paragraph={{ rows: 42 }} />
              </div>
            </td>
            <td />
          </tr>
        </tbody>
      );

    if (!records?.length)
      return (
        <tbody>
          <tr>
            <td
              colSpan={2}
              style={{
                borderTop: '1px solid #E8E8E8',
              }}
            >
              <Empty
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: calculateEmptyWrapperHeight(),
                  justifyContent: 'center',
                }}
              />
            </td>
            <td />
          </tr>
        </tbody>
      );

    return (
      <tbody>
        {records?.length &&
          records.map((record, index) => renderRow(record, index))}
      </tbody>
    );
  };

  return renderBody();
};

export const TypeIDWrapper = styled.div<{
  experimentchoiceindex?: number;
  valuelength?: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  min-height: 32px;
  height: 100%;
  width: ${(props) => (props.valuelength === 4 ? '48px' : '32px')};
  min-width: ${(props) => (props.valuelength === 4 ? '48px' : '32px')};
  color: ${(props) =>
    !props?.experimentchoiceindex || props.experimentchoiceindex > 0
      ? '#3D3D3D'
      : '#FF806B'};
`;

export const ValueWrapper = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  border-left: 1px solid #e8e8e8;
  font-size: 14px;
  line-height: 20px;
  min-height: 32px;
  padding: 0;
`;

export default VariablesSearchRecords;
